import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import useMutationApi from 'src/api/useMutationApi';
import { CoreEnumOptionAssociation } from 'src/api/core/enumOptionAssociations/useEnumOptionAssociations';

interface BulkUpdateData {
  entityType: string;
  entityId: string;
  enumOptionIds: string[];
}

/**
 * Hook for managing product group tag mutations
 * Handles adding and removing tags for product groups with proper cache invalidation
 */
export function useProductGroupTagMutations() {
  const queryClient = useQueryClient();

  // Track pending mutations per product group (for loading states)
  const [tagsUpdatingByProductGroupId, setTagsUpdatingByProductGroupId] =
    useState<Map<string, boolean>>(new Map());

  // Generic mutation for updating product group tags
  const updateProductGroupTagsMutation = useMutationApi<
    BulkUpdateData,
    CoreEnumOptionAssociation[]
  >({
    endpoint: 'core/enum-option-associations/bulk',
    method: 'PUT',
    successMessage: 'Tags updated successfully',
    errorMessage: 'Failed to update tags',
    options: {
      onSuccess: (_, variables) => {
        const productGroupId = variables.entityId;
        // Invalidate individual query
        queryClient.invalidateQueries({
          queryKey: [
            'enumOptionAssociations',
            'rebate_product_group',
            productGroupId,
          ],
        });
        // Invalidate bulk query to update UI
        queryClient.invalidateQueries({
          queryKey: ['core/enum-option-associations/bulk-list'],
        });
        // Invalidate product groups with tags query - use predicate to match any supplier
        queryClient.invalidateQueries({
          predicate: (query) => {
            const key = query.queryKey[0];
            return (
              typeof key === 'string' &&
              key.includes('rebates/product-groups/supplier') &&
              key.includes('/with-tags')
            );
          },
        });
        setTagsUpdatingByProductGroupId((prev) => {
          const next = new Map(prev);
          next.set(productGroupId, false);
          return next;
        });
      },
      onError: (_, variables) => {
        const productGroupId = variables.entityId;
        setTagsUpdatingByProductGroupId((prev) => {
          const next = new Map(prev);
          next.set(productGroupId, false);
          return next;
        });
      },
    },
  });

  const handleProductGroupTagAdd = useCallback(
    (productGroupId: string, tagId: string, currentTagIds: string[]) => {
      const tagSet = new Set(currentTagIds);

      if (!tagSet.has(tagId)) {
        setTagsUpdatingByProductGroupId((prev) => {
          const next = new Map(prev);
          next.set(productGroupId, true);
          return next;
        });

        updateProductGroupTagsMutation.mutate({
          entityType: 'rebate_product_group',
          entityId: productGroupId,
          enumOptionIds: [...tagSet, tagId],
        });
      }
    },
    [updateProductGroupTagsMutation],
  );

  const handleProductGroupTagRemove = useCallback(
    (productGroupId: string, tagId: string, currentTagIds: string[]) => {
      const newTagIds = currentTagIds.filter((id) => id !== tagId);

      setTagsUpdatingByProductGroupId((prev) => {
        const next = new Map(prev);
        next.set(productGroupId, true);
        return next;
      });

      updateProductGroupTagsMutation.mutate({
        entityType: 'rebate_product_group',
        entityId: productGroupId,
        enumOptionIds: newTagIds,
      });
    },
    [updateProductGroupTagsMutation],
  );

  return {
    handleProductGroupTagAdd,
    handleProductGroupTagRemove,
    tagsUpdatingByProductGroupId,
  };
}
